import React, { useState, useEffect } from "react";
import { Modal, Form, Row, Col, Alert } from "react-bootstrap";
import {
  Card as AntCard,
  Typography,
  Divider,
  Space,
  Button as AntButton,
  Tag,
  Form as AntForm,
  Input as AntInput,
  Select,
  Upload,
} from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import { useEmailContext } from "./EmailProvider";
import useEmails, { normalizeEmailMessage } from "hooks/emails/useEmails";
import { useFileUpload } from "hooks/useFileUpload";
import useDebounce from "hooks/route-rules/useDebounce";
import { useUserManagement } from "hooks";

const mapUniqueUserOptions = (results) => {
  const seen = new Set();
  return (Array.isArray(results) ? results : []).reduce((acc, user) => {
    const email = user?.email;
    if (!email || seen.has(email)) return acc;
    seen.add(email);
    acc.push({
      label: `${user.firstName} ${user.lastName} (${email})`,
      value: email,
    });
    return acc;
  }, []);
};

const ComposeEmail = ({ show, onHide, asPage = false }) => {
  const { emailDispatch } = useEmailContext();
  const { sendEmail, loading } = useEmails();
  const { uploadFile, uploading: uploadingFiles } = useFileUpload();

  const [formData, setFormData] = useState({
    to: [],
    cc: [],
    bcc: [],
    subject: "",
    body: "",
    attachments: [],
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const { searchUserProfiles } = useUserManagement();

  const [toSearchTerm, setToSearchTerm] = useState("");
  const [ccSearchTerm, setCcSearchTerm] = useState("");
  const [bccSearchTerm, setBccSearchTerm] = useState("");

  const debouncedToSearch = useDebounce(toSearchTerm, 300);
  const debouncedCcSearch = useDebounce(ccSearchTerm, 300);
  const debouncedBccSearch = useDebounce(bccSearchTerm, 300);

  const [toOptions, setToOptions] = useState([]);
  const [ccOptions, setCcOptions] = useState([]);
  const [bccOptions, setBccOptions] = useState([]);

  const [isSearchingTo, setIsSearchingTo] = useState(false);
  const [isSearchingCc, setIsSearchingCc] = useState(false);
  const [isSearchingBcc, setIsSearchingBcc] = useState(false);

  const [uploadList, setUploadList] = useState([]);

 const formFooter = (
    <Space>
      <AntButton onClick={onHide} disabled={isSubmitting}>
        Cancel
      </AntButton>
      <AntButton
        type="primary"
        htmlType="submit"
        loading={isSubmitting || loading}
      >
        <FontAwesomeIcon icon="paper-plane" className="me-2" />
        Send Email
      </AntButton>
    </Space>
  );

  useEffect(() => {
    const searchUsers = async (searchTerm, setOptions, setIsSearching) => {
      if (searchTerm.trim()) {
        setIsSearching(true);
        try {
          const results = await searchUserProfiles(searchTerm.trim());
          const mapped = mapUniqueUserOptions(results);
          setOptions(mapped);
        } catch (error) {
          console.error("Error searching users:", error);
          setOptions([]);
        } finally {
          setIsSearching(false);
        }
      } else {
        setOptions([]);
      }
    };

    searchUsers(debouncedToSearch, setToOptions, setIsSearchingTo);
  }, [debouncedToSearch, searchUserProfiles]);

  useEffect(() => {
    const searchUsers = async (searchTerm, setOptions, setIsSearching) => {
      if (searchTerm.trim()) {
        setIsSearching(true);
        try {
          const results = await searchUserProfiles(searchTerm.trim());
          const mapped = mapUniqueUserOptions(results);
          setOptions(mapped);
        } catch (error) {
          console.error("Error searching users:", error);
          setOptions([]);
        } finally {
          setIsSearching(false);
        }
      } else {
        setOptions([]);
      }
    };

    searchUsers(debouncedCcSearch, setCcOptions, setIsSearchingCc);
  }, [debouncedCcSearch, searchUserProfiles]);

  useEffect(() => {
    const searchUsers = async (searchTerm, setOptions, setIsSearching) => {
      if (searchTerm.trim()) {
        setIsSearching(true);
        try {
          const results = await searchUserProfiles(searchTerm.trim());
          const mapped = mapUniqueUserOptions(results);
          setOptions(mapped);
        } catch (error) {
          console.error("Error searching users:", error);
          setOptions([]);
        } finally {
          setIsSearching(false);
        }
      } else {
        setOptions([]);
      }
    };

    searchUsers(debouncedBccSearch, setBccOptions, setIsSearchingBcc);
  }, [debouncedBccSearch, searchUserProfiles]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();

    if (formData.to.length === 0) {
      setError("Please select at least one recipient");
      return;
    }

    if (!formData.subject.trim()) {
      setError("Subject is required");
      return;
    }

    if (!formData.body.trim()) {
      setError("Email body is required");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      let attachmentIds = [];
      if (uploadList.length) {
        try {
          attachmentIds = [];
          for (const item of uploadList) {
            const file = item.originFileObj;
            if (!file) continue;
            const result = await uploadFile(null, file, {
              purpose: "USER_PROFILE",
              fileType: "DOCUMENT",
            });
            if (result?.fileId) attachmentIds.push(result.fileId);
          }
        } catch (uploadErr) {
          setError(uploadErr.message || "Failed to upload attachments");
          setIsSubmitting(false);
          return;
        }
      }

      const apiPayload = {
        title: formData.subject.trim(),
        recipients: formData.to || [],
        cc: formData.cc || [],
        bcc: formData.bcc || [],
        body: formData.body.trim(),
        attachments: attachmentIds,
      };

      await sendEmail(apiPayload);

      // Keep title/to for display in SentEmails
      const sentEmail = normalizeEmailMessage({
        ...apiPayload,
        subject: apiPayload.title,
        sentAt: new Date().toISOString(),
        status: "SENT",
        data: apiPayload,
      });

      emailDispatch({ type: "ADD_SENT_EMAIL", payload: sentEmail });

      toast.success("Email sent successfully!");

      setFormData({
        to: [],
        cc: [],
        bcc: [],
        subject: "",
        body: "",
        attachments: [],
      });
      setUploadList([]);

      onHide();
    } catch (err) {
      console.error("Failed to send email:", err);
      setError(err.message || "Failed to send email");
      toast.error("Failed to send email");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formContent = (
    <>
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="">
          <Typography.Title level={5} style={{ margin: 0 }}>
            New message
          </Typography.Title>
        </div>
        <Typography.Text type="secondary">
          Sender: no-reply@oliveeb.com
        </Typography.Text>
      </div>

      <Divider style={{ margin: "12px 0" }} />

      <AntForm layout="vertical" component={false}>
        <div className="text-uppercase text-muted small mb-2 mt-2">
          Recipients
        </div>
        <AntForm.Item label="To" required>
          <Select
            mode="multiple"
            placeholder="Search and add recipients"
            value={formData.to}
            onChange={(value) => {
              handleInputChange("to", value);
              setToSearchTerm("");
            }}
            options={toOptions}
            loading={isSearchingTo}
            disabled={isSubmitting}
            notFoundContent={
              toSearchTerm && !isSearchingTo ? "No users found" : null
            }
            showSearch={{
              autoClearSearchValue:false,
              searchValue:toSearchTerm,
              onSearch: setToSearchTerm,
              filterOption: false,
            }}
          />
        </AntForm.Item>

        <Row className="mb-1">
          <Col md={6}>
            <AntForm.Item label="CC">
              <Select
                mode="multiple"
                placeholder="Search CC recipients"
                value={formData.cc}
                onChange={(value) => {
                  handleInputChange("cc", value);
                  setCcSearchTerm("");
                }}
                showSearch={{
                  onSearch: setCcSearchTerm,
                  autoClearSearchValue:false,
                  filterOption: false,
                  searchValue:ccSearchTerm,
                }}
                options={ccOptions}
                loading={isSearchingCc}
               
                disabled={isSubmitting}
                notFoundContent={
                  ccSearchTerm && !isSearchingCc ? "No users found" : null
                }
              />
            </AntForm.Item>
          </Col>
          <Col md={6}>
            <AntForm.Item label="BCC">
              <Select
                mode="multiple"
                placeholder="Search BCC recipients"
                value={formData.bcc}
                onChange={(value) => {
                  handleInputChange("bcc", value);
                  setBccSearchTerm("");
                }}
                showSearch={{
                  onSearch: setBccSearchTerm,
                  filterOption: false,
                  autoClearSearchValue:false,
                  searchValue:bccSearchTerm,
                }}
                options={bccOptions}
                loading={isSearchingBcc}
                disabled={isSubmitting}
                notFoundContent={
                  bccSearchTerm && !isSearchingBcc ? "No users found" : null
                }
              />
            </AntForm.Item>
          </Col>
        </Row>

        <div className="text-uppercase text-muted small mb-2">Content</div>
        <AntForm.Item label="Subject" required>
          <AntInput
            placeholder="Enter email subject..."
            value={formData.subject}
            onChange={(e) => handleInputChange("subject", e.target.value)}
            disabled={isSubmitting}
          />
        </AntForm.Item>

        <AntForm.Item label="Message" required>
          <AntInput.TextArea
            rows={8}
            placeholder="Compose your message..."
            value={formData.body}
            onChange={(e) => handleInputChange("body", e.target.value)}
            disabled={isSubmitting}
          />
        </AntForm.Item>

        {/* <Divider style={{ margin: "12px 0" }} />
        <div className="text-uppercase text-muted small mb-2">Attachments</div>
        <AntForm.Item>
          <Upload
            multiple
            fileList={uploadList}
            beforeUpload={() => false}
            onChange={({ fileList }) => {
              setUploadList(fileList);
              setFormData((prev) => ({
                ...prev,
                attachments: [],
              }));
            }}
          >
            <AntButton loading={uploadingFiles}>Upload files</AntButton>
          </Upload>
          <div className="text-muted small mt-1">
            PDF, DOC, DOCX, TXT, JPG, PNG, GIF. Max 10MB total.
          </div>
        </AntForm.Item> */}
        <div className="d-flex justify-content-end gap-2 mt-3">
          {formFooter}
        </div>
      </AntForm>
    </>
  );

 

  if (asPage) {
    return (
      <Form onSubmit={handleSubmit}>
        <AntCard className="shadow-sm border-0">{formContent}</AntCard>
      </Form>
    );
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="bg-light">
        <Modal.Title>
          <FontAwesomeIcon icon="fa-envelope" className="me-2" />
          Compose Email
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body className="p-4">
          <AntCard className="shadow-sm border-0">{formContent}</AntCard>
        </Modal.Body>
      </Form>
    </Modal>
  );
};

export default ComposeEmail;
